import { useFormikContext } from 'formik';
import { useMemo, useState } from 'react';
import {
  Button,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@spotted-zebra-uk/ui-components';
import { getFiltersCount } from '../../Projects.helpers';
import DatesTabContent from './DatesTabContent';
import styles from './ProjectFilterModal.module.scss';
import {
  emptyProjectFilterFormValues,
  FiltersFormValues,
} from './ProjectsFilterModal';
import { PROJECTS_FILTER_TABS } from './ProjectsFilterModal.constants';
import {
  getFirstInvalidTabIndex,
  hasTabError,
} from './ProjectsFilterModal.helpers';
import RoleTabContent from './RoleTabContent';
import StagesTabContent from './StagesTabContent';

export const PROJECTS_FILTER_MODAL_DATA_TEST_ID = 'projects-page-filter-modal';

const TAB_CONTENTS = [RoleTabContent, StagesTabContent, DatesTabContent];

const ProjectsFilterModalForm = ({ onCancel }: { onCancel: () => void }) => {
  const { values, errors, submitForm, setValues, validateForm } =
    useFormikContext<FiltersFormValues>();

  // The modal always opens on Role; after that the tab is only moved by the
  // user, or by Apply landing on the first tab with an invalid field.
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  const totalFilterCount = getFiltersCount(values);

  const tabFilterCounts = useMemo(
    () =>
      PROJECTS_FILTER_TABS.map(filterTab =>
        getFiltersCount(values, filterTab.fieldNames)
      ),
    [values]
  );

  const handleApply = async () => {
    const validationErrors = await validateForm();
    const invalidTabIndex = getFirstInvalidTabIndex(validationErrors);

    if (invalidTabIndex !== -1) {
      setSelectedTabIndex(invalidTabIndex);
    }

    await submitForm();
  };

  const handleResetFilters = async () => {
    setValues(emptyProjectFilterFormValues);
    await validateForm();
    submitForm();
  };

  return (
    <form
      className={styles.form}
      onSubmit={event => event.preventDefault()}
      data-testid={`${PROJECTS_FILTER_MODAL_DATA_TEST_ID}-form`}
    >
      <Tabs
        className={styles.tabs}
        selectedTabIndex={selectedTabIndex}
        onSelectTab={setSelectedTabIndex}
      >
        <TabList ariaLabel="Filter categories">
          {PROJECTS_FILTER_TABS.map((filterTab, tabIndex) => (
            <Tab
              counter={tabFilterCounts[tabIndex] || undefined}
              hasError={hasTabError(errors, tabIndex)}
              key={filterTab.key}
            >
              {filterTab.title}
            </Tab>
          ))}
        </TabList>
        <TabPanels>
          {TAB_CONTENTS.map((TabContent, tabIndex) => (
            <TabPanel
              className={styles.tabPanel}
              key={PROJECTS_FILTER_TABS[tabIndex].key}
            >
              <TabContent />
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>

      <div className={styles.actions}>
        <div>
          <Button variant="tertiary" onClick={handleResetFilters}>
            Reset all filters
          </Button>
        </div>
        <div className={styles.actionsRight}>
          <Button
            type="button"
            size="medium"
            variant="secondary"
            onClick={onCancel}
            aria-label="Cancel and close filter modal"
          >
            Cancel
          </Button>
          <Button
            size="medium"
            type="submit"
            onClick={handleApply}
            aria-label="Apply filters"
            counter={totalFilterCount || undefined}
            data-testid={`${PROJECTS_FILTER_MODAL_DATA_TEST_ID}-apply-button`}
          >
            Apply
          </Button>
        </div>
      </div>
    </form>
  );
};

export default ProjectsFilterModalForm;
